import React, { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { setBarrioCoords, clearBarrioCoords } from '../../services/territorioService';

// Centro de San Nicolás de los Arroyos.
const SN_CENTER = [-33.3358, -60.2136];
const SN_ZOOM = 13;

// Color del BORDE del área según lo accionable (estado de gestión).
function colorDe(b) {
  const abiertos = b.reclamos_abiertos || 0;
  if (abiertos >= 3) return '#ef4444';
  if (abiertos >= 1) return '#f59e0b';
  return '#22c55e';
}

// Paleta de RELLENO — un color distinto por barrio para diferenciarlos.
// Evita verde/naranja/rojo (reservados al borde/estado) para que no se confundan.
const PALETTE = ['#2563eb', '#7c3aed', '#0891b2', '#db2777', '#4f46e5', '#0d9488',
  '#c026d3', '#f472b6', '#6366f1', '#e11d48', '#3b82f6', '#a21caf'];
const RADIO_M = 230; // radio aproximado del área de cada barrio, en metros

function iconoDe(color) {
  return L.divIcon({
    className: 'barrio-pin',
    html: `<div style="width:16px;height:16px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.35);cursor:move"></div>`,
    iconSize: [16, 16], iconAnchor: [8, 8], popupAnchor: [0, -8],
  });
}

function popupHtml(b) {
  const vis = b.dias_sin_visita == null ? 'sin recorridas registradas'
    : b.dias_sin_visita === 0 ? 'recorrido hoy' : `${b.dias_sin_visita} días sin recorrer`;
  return `<div style="font-family:system-ui;font-size:13px;line-height:1.5">
    <b>${b.nombre}</b><br/>
    🔴 ${b.reclamos_abiertos || 0} abiertos · ✅ ${b.reclamos_resueltos || 0} resueltos<br/>
    <span style="color:#64748b">🚶 ${vis}</span>
  </div>`;
}

export default function MapaBarrios({ barrios = [], onCoordsSaved, onSelectBarrio }) {
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const layerRef = useRef(null);
  const [editando, setEditando] = useState(false);
  const [ubicando, setUbicando] = useState(null); // barrio a colocar con el próximo click
  const [msg, setMsg] = useState(null);
  const [limpiando, setLimpiando] = useState(false);

  const conCoords = barrios.filter((b) => b.lat != null && b.lng != null);
  const sinCoords = barrios.filter((b) => b.lat == null || b.lng == null);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(null), 2500); };

  const guardar = useCallback(async (id, lat, lng) => {
    try { await setBarrioCoords(id, lat, lng); flash('Ubicación guardada'); if (onCoordsSaved) onCoordsSaved(); }
    catch (e) { flash(e?.response?.data?.error || 'No se pudo guardar'); }
  }, [onCoordsSaved]);

  async function borrarUno(id, e) {
    if (e) e.stopPropagation();
    try { await clearBarrioCoords(id); if (onCoordsSaved) onCoordsSaved(); } catch { flash('No se pudo borrar'); }
  }

  async function borrarTodo() {
    if (!window.confirm('¿Borrar la ubicación de todos los barrios para volver a colocarlos a mano?')) return;
    setLimpiando(true);
    try {
      for (const b of conCoords) { await clearBarrioCoords(b.id); }
      flash('Listo — colocá cada barrio tocando su nombre y después el mapa');
      if (onCoordsSaved) onCoordsSaved();
    } catch { flash('No se pudieron borrar todas'); }
    finally { setLimpiando(false); }
  }

  // Init del mapa (una vez).
  useEffect(() => {
    if (mapObj.current || !mapRef.current) return;
    // Estilo de las etiquetas de barrio (nombre sobre el área): transparente y legible.
    if (!document.getElementById('barrio-label-style')) {
      const st = document.createElement('style');
      st.id = 'barrio-label-style';
      st.textContent = '.barrio-label{background:transparent;border:none;box-shadow:none;color:#0b1320;font-weight:700;font-size:11px;text-shadow:0 0 3px #fff,0 0 3px #fff,0 0 3px #fff}.barrio-label::before{display:none}';
      document.head.appendChild(st);
    }
    const map = L.map(mapRef.current, { scrollWheelZoom: true }).setView(SN_CENTER, SN_ZOOM);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap', maxZoom: 19,
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapObj.current = map;
    setTimeout(() => map.invalidateSize(), 60);
    return () => { map.remove(); mapObj.current = null; };
  }, []);

  // Click en el mapa: si hay un barrio "ubicando", lo coloca.
  useEffect(() => {
    const map = mapObj.current;
    if (!map) return;
    const onClick = (e) => {
      if (!ubicando) return;
      const { lat, lng } = e.latlng;
      guardar(ubicando.id, lat, lng);
      setUbicando(null);
    };
    map.on('click', onClick);
    return () => map.off('click', onClick);
  }, [ubicando, guardar]);

  // Redibujar áreas cuando cambian los datos o el modo edición.
  useEffect(() => {
    const map = mapObj.current, group = layerRef.current;
    if (!map || !group) return;
    group.clearLayers();
    conCoords.forEach((b, i) => {
      const borde = colorDe(b);           // estado de gestión
      const relleno = PALETTE[i % PALETTE.length]; // distingue el barrio
      const area = L.circle([b.lat, b.lng], {
        radius: RADIO_M, color: borde, weight: 3, fillColor: relleno, fillOpacity: 0.35,
      });
      // Nombre al pasar el mouse (con 92 barrios, etiquetas fijas serían ilegibles).
      area.bindTooltip(b.nombre, { direction: 'top', className: 'barrio-label', sticky: true });
      // Click → panel de inteligencia del barrio (o popup si no hay handler).
      if (onSelectBarrio) area.on('click', () => { if (!editando) onSelectBarrio(b); });
      else area.bindPopup(popupHtml(b));
      group.addLayer(area);
      // En modo edición, un pin arrastrable en el centro para reubicar el área.
      if (editando) {
        const m = L.marker([b.lat, b.lng], { icon: iconoDe(borde), draggable: true });
        m.on('dragend', (e) => { const p = e.target.getLatLng(); guardar(b.id, p.lat, p.lng); });
        group.addLayer(m);
      }
    });
    setTimeout(() => map.invalidateSize(), 30);
  }, [barrios, editando, guardar]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <button type="button" onClick={() => { setEditando((v) => !v); setUbicando(null); }}
          style={{ background: editando ? '#22c55e' : 'transparent', color: editando ? '#0b1320' : 'var(--text-secondary)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 6, padding: '0.35rem 0.7rem', cursor: 'pointer', fontSize: '0.8rem' }}>
          {editando ? '✓ Listo (arrastrá los pines)' : '✎ Ubicar / mover barrios'}
        </button>
        {conCoords.length > 0 && (
          <button type="button" onClick={borrarTodo} disabled={limpiando}
            style={{ background: 'transparent', color: 'var(--text-secondary)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 6, padding: '0.35rem 0.7rem', cursor: 'pointer', fontSize: '0.8rem' }}>
            {limpiando ? 'Borrando…' : '↺ Reubicar todo (borrar)'}
          </button>
        )}
        <span style={{ fontSize: '0.74rem', color: 'var(--text-secondary)' }}>
          Borde = estado:{' '}
          <span style={{ color: '#22c55e' }}>●</span> al día ·{' '}
          <span style={{ color: '#f59e0b' }}>●</span> con reclamos ·{' '}
          <span style={{ color: '#ef4444' }}>●</span> atención (3+) · relleno = cada barrio
        </span>
        {msg && <span style={{ fontSize: '0.76rem', color: 'var(--accent, #38d39f)' }}>{msg}</span>}
      </div>

      {ubicando && (
        <div style={{ fontSize: '0.8rem', color: '#f59e0b', marginBottom: 6 }}>
          📍 Tocá en el mapa dónde queda <b>{ubicando.nombre}</b> (o <button onClick={() => setUbicando(null)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', textDecoration: 'underline' }}>cancelar</button>)
        </div>
      )}

      <div ref={mapRef} style={{ height: 460, width: '100%', borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }} />

      {sinCoords.length > 0 && (
        <div style={{ marginTop: 10, fontSize: '0.82rem' }}>
          <div style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>
            Sin ubicar ({sinCoords.length}) — tocá un barrio y después el punto exacto en el mapa:
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {sinCoords.map((b) => (
              <button key={b.id} type="button" onClick={() => { setEditando(false); setUbicando(b); }}
                style={{ background: ubicando?.id === b.id ? 'rgba(245,158,11,0.35)' : 'rgba(245,158,11,0.1)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 6, padding: '0.25rem 0.55rem', cursor: 'pointer', fontSize: '0.78rem' }}>
                📍 {b.nombre}
              </button>
            ))}
          </div>
        </div>
      )}

      {editando && conCoords.length > 0 && (
        <div style={{ marginTop: 8, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
          Arrastrá cualquier pin para corregir. Para sacar uno mal puesto:{' '}
          {conCoords.map((b) => (
            <button key={b.id} type="button" onClick={(e) => borrarUno(b.id, e)}
              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.78rem', padding: '0 4px' }}>
              ✕ {b.nombre}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
